import React from 'react';

export interface ShellStep {
  label: string;
}

/** Each studio runs on its own demo machine, so each gets its own accent. */
export type Studio = 'product' | 'commercial' | 'builder';

interface EditorialShellProps {
  studio: Studio;
  chapter: string;
  steps: ShellStep[];
  currentStep: number;
  onStepSelect?: (step: number) => void;
  wide?: boolean;
  actions?: React.ReactNode;
  actionNote?: string;
  children: React.ReactNode;
}

const pad = (n: number) => String(n).padStart(2, '0');

const EditorialShell: React.FC<EditorialShellProps> = ({
  studio,
  chapter,
  steps,
  currentStep,
  onStepSelect,
  wide = false,
  actions,
  actionNote,
  children,
}) => {
  const progress = steps.length > 1 ? ((currentStep - 1) / (steps.length - 1)) * 100 : 100;
  const currentLabel = steps[currentStep - 1]?.label ?? '';

  return (
    <div className="ed-shell" data-studio={studio}>
      <aside className="ed-rail">
        <div className="ed-rail-brand">
          <img src="/superpossible_logo.svg" alt="SuperPossible" />
          <span className="ed-chapter">{chapter}</span>
        </div>

        <ol className="ed-index">
          {steps.map((item, i) => {
            const index = i + 1;
            const isCurrent = index === currentStep;
            const isDone = index < currentStep;
            const canJump = Boolean(onStepSelect) && isDone;

            return (
              <li key={item.label}>
                <button
                  type="button"
                  className={`ed-index-item${isCurrent ? ' is-current' : ''}${isDone ? ' is-done' : ''}`}
                  data-clickable={canJump}
                  onClick={canJump ? () => onStepSelect?.(index) : undefined}
                  disabled={!canJump}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className="ed-index-num">{isDone ? '—' : pad(index)}</span>
                  <span className="ed-index-label">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <span className="ed-rail-foot">
          {pad(currentStep)} / {pad(steps.length)}
        </span>

        <div className="ed-rail-compact">
          <span className="ed-rail-compact-label">
            {pad(currentStep)} — {currentLabel}
          </span>
          <div className="ed-rail-compact-track">
            <div className="ed-rail-compact-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </aside>

      <div className="ed-main">
        <main className={`ed-canvas${wide ? ' ed-canvas--wide' : ''}`}>{children}</main>

        {actions && (
          <div className="ed-actionbar">
            <div className="ed-actionbar-inner">{actions}</div>
          </div>
        )}
        {!actions && actionNote && (
          <div className="ed-actionbar">
            <div className="ed-actionbar-inner">
              <span className="ed-actionbar-note">{actionNote}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorialShell;
